// Comercial › Painel › "Onde a Hydrodata ganha" (Ciclo 1 da Bcorp, semanas 2 e 3).
// Os NÚMEROS são calculados ao vivo pelo servidor (og_obter). Os TEXTOS são da IA e ficam
// gravados com data; o botão "Atualizar com IA" pede um texto novo (og_atualizar) e a página
// acompanha o andamento (og_status) a cada 4 segundos. Contas e regras da tela: ondeGanhaRegras.ts.
// Contrato e limites: docs/onde-a-hydrodata-ganha.md.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Alert, App as AntApp, Button, Card, Col, Descriptions, Input, Modal, Row, Segmented, Select, Skeleton, Space, Spin, Table, Tag,
  Tooltip as Dica, Typography, type TableColumnsType,
} from 'antd';
import { EditOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { usePerms } from '../../hooks/usePerms';
import {
  atualizar, ehConflito, ehEspera, erroDe, obter, salvarSituacao, status as consultarStatus,
  type CandidatoSemana3, type Caso, type Obter, type TextoIa,
} from './api';
import BaseFinanceira from './BaseFinanceira';
import {
  aprendizadosDaBase, type ResumoBase,
  barrasDeSituacao, CHAVE_ATUALIZACAO, contarCasos, dataBr, erroDaEdicao, fechouDeTexto, fechouTexto, filtrarCasos,
  filtroValido, INTERVALO_CONSULTA_MS, LIMITE_LINHA, MAX_FALHAS_SEGUIDAS, linhaDoRetrato, maioresSituacoes, numerosMudaram, ocupado, opcoesDeGrupo, ordenarCandidatos,
  pct, podeConsultar, proximoPasso, reais, reaisCurto, rotuloGrupo, ROTULO_RELACAO, somaDasNotas, TEXTO_ESPERA, TEXTO_NUMEROS_MUDARAM,
  TEXTO_SEM_RETRATO, textoContagem, textoQuando, textoTop3, TODAS_AS_SITUACOES, totalDaReuniao, leituraDaSituacao,
  semAcento, type BarraSituacao, type Fase, type FiltroStatus,
} from './ondeGanhaRegras';

const { Title, Text, Paragraph } = Typography;
const PERM_VER = 'comercial.dashboard.ver';
const PERM_DRE = 'financeiro.dre.ver';

// Busca dos Selects: ignora acento e caixa ("eletrico" acha "elétrico").
const filtrarOpcao = (digitado: string, opcao?: { label?: unknown }): boolean =>
  semAcento(String(opcao?.label ?? '')).includes(semAcento(digitado));
const COR_GANHO = '#1677ff';
const COR_PERDIDO = '#bae0ff';
const COR_RECEITA = '#389e0d';

// ── Pequenos apoios ──────────────────────────────────────────────────────────
function lerIdGuardado(): number | null {
  try {
    const n = Number(sessionStorage.getItem(CHAVE_ATUALIZACAO));
    return Number.isSafeInteger(n) && n > 0 ? n : null;
  } catch { return null; }
}

function guardarId(id: number | null) {
  try {
    if (id) sessionStorage.setItem(CHAVE_ATUALIZACAO, String(id));
    else sessionStorage.removeItem(CHAVE_ATUALIZACAO);
  } catch { /* sem armazenamento da aba: a página acompanha do mesmo jeito */ }
}

const lista = <T,>(v: T[] | null | undefined): T[] => (Array.isArray(v) ? v : []);
const cortar = (t: string, n: number): string => (t.length > n ? `${t.slice(0, n - 1)}…` : t);

const CAIXA_TOOLTIP = {
  background: '#fff', border: '1px solid #d9d9d9', borderRadius: 6, padding: '8px 12px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,.12)',
} as const;

function Secao({ id, etiqueta, titulo, subtitulo, children }: { id: string; etiqueta?: string; titulo: string; subtitulo?: ReactNode; children: ReactNode }) {
  return (
    <section aria-labelledby={id} style={{ marginTop: 32 }}>
      {etiqueta ? <Text type="secondary" style={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 12 }}>{etiqueta}</Text> : null}
      <Title level={3} id={id} style={{ marginTop: 0 }}>{titulo}</Title>
      {subtitulo ? <Paragraph type="secondary" style={{ maxWidth: 900 }}>{subtitulo}</Paragraph> : null}
      {children}
    </section>
  );
}

function SemTexto() {
  return <Alert type="info" showIcon title={TEXTO_SEM_RETRATO} />;
}

/** Gráfico com rolagem própria no celular: o resto da página não sai do lugar. */
function AreaDoGrafico({ altura, larguraMinima = 560, rotulo, children }: { altura: number; larguraMinima?: number; rotulo: string; children: ReactNode }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <div role="img" aria-label={rotulo} style={{ minWidth: larguraMinima, height: altura }}>{children}</div>
    </div>
  );
}

const corDaNota = (n: number | null): string => (n === 3 ? 'green' : n === 2 ? 'gold' : n === 1 ? 'default' : 'default');

function CelulaNota({ nota, motivo }: { nota: number | null; motivo: string }) {
  return (
    <div>
      {nota == null ? <Tag>a decidir</Tag> : <Tag color={corDaNota(nota)} style={{ fontWeight: 600 }}>{nota}</Tag>}
      <div><Text type="secondary" style={{ fontSize: 12 }}>{motivo}</Text></div>
    </div>
  );
}

// ── Gráficos das situações ───────────────────────────────────────────────────
function TooltipFechamento({ active, payload }: { active?: boolean; payload?: any[] }) {
  if (!active || !payload || !payload.length) return null;
  const b: BarraSituacao = payload[0].payload;
  return (
    <div style={CAIXA_TOOLTIP}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{b.nome}</div>
      <div>Ganhos: {b.ganhos}</div>
      <div>Perdidos: {b.perdidos}</div>
      <div>Fecha {pct(b.taxa)}</div>
    </div>
  );
}

function TooltipReceita({ active, payload }: { active?: boolean; payload?: any[] }) {
  if (!active || !payload || !payload.length) return null;
  const b: BarraSituacao = payload[0].payload;
  return (
    <div style={CAIXA_TOOLTIP}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{b.nome}</div>
      <div>{reais(b.receita)}</div>
      <div>{b.receita > 0 ? textoTop3(b.top3_pct) : 'Sem negócio ganho'}</div>
    </div>
  );
}

function GraficoFechamento({ barras }: { barras: BarraSituacao[] }) {
  const altura = Math.max(160, barras.length * 36 + 56);
  return (
    <AreaDoGrafico altura={altura} rotulo="Ganhos e perdidos por situação">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={barras} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid horizontal={false} stroke="#eee" strokeDasharray="3 3" />
          <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
          <YAxis type="category" dataKey="nome" width={230} tick={{ fontSize: 12 }} tickFormatter={(v: string) => cortar(v, 44)} interval={0} />
          <Tooltip content={(p: any) => <TooltipFechamento active={p.active} payload={p.payload} />} cursor={{ fill: 'rgba(0,0,0,.04)' }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="ganhos" name="Ganhos" stackId="casos" fill={COR_GANHO} maxBarSize={24} isAnimationActive={false} />
          <Bar dataKey="perdidos" name="Perdidos" stackId="casos" fill={COR_PERDIDO} maxBarSize={24} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </AreaDoGrafico>
  );
}

function GraficoReceita({ barras }: { barras: BarraSituacao[] }) {
  const ordenadas = useMemo(() => [...barras].sort((a, b) => b.receita - a.receita), [barras]);
  const altura = Math.max(160, ordenadas.length * 36 + 40);
  return (
    <AreaDoGrafico altura={altura} rotulo="Receita fechada por situação">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={ordenadas} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid horizontal={false} stroke="#eee" strokeDasharray="3 3" />
          <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v: number) => reaisCurto(v)} />
          <YAxis type="category" dataKey="nome" width={230} tick={{ fontSize: 12 }} tickFormatter={(v: string) => cortar(v, 44)} interval={0} />
          <Tooltip content={(p: any) => <TooltipReceita active={p.active} payload={p.payload} />} cursor={{ fill: 'rgba(0,0,0,.04)' }} />
          <Bar dataKey="receita" name="Receita fechada" fill={COR_RECEITA} maxBarSize={24} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </AreaDoGrafico>
  );
}

// ── A página ─────────────────────────────────────────────────────────────────
export default function OndeGanha() {
  const { pode, carregando: carregandoPerms, erro: erroPerms, recarregar } = usePerms();
  const { message } = AntApp.useApp();
  const liberado = pode(PERM_VER);
  const veDre = pode(PERM_DRE);
  // 18/10/2026: investido e lucro final em "O que aprendemos" — só para quem vê a DRE, por regra (sem IA).
  const [baseFin, setBaseFin] = useState<{ r: ResumoBase | null; periodo: string }>({ r: null, periodo: '' });
  const aoResumoFinanceiro = useCallback((r: ResumoBase | null, periodo: string) => setBaseFin({ r, periodo }), []);
  const aprendizadosFin = veDre && baseFin.r ? aprendizadosDaBase(baseFin.r, baseFin.periodo) : [];

  const [dados, setDados] = useState<Obter | null>(null);
  const [erroLeitura, setErroLeitura] = useState<{ texto: string; status: number | null } | null>(null);
  const [lendo, setLendo] = useState(false);
  const [fase, setFase] = useState<Fase>('parado');
  const [atualizacaoId, setAtualizacaoId] = useState<number | null>(null);
  const [aviso, setAviso] = useState<{ tipo: 'erro' | 'aviso'; texto: string; conferir?: boolean } | null>(null);
  const [consultaErro, setConsultaErro] = useState<string | null>(null);
  const emConsulta = useRef(false);
  const falhasSeguidas = useRef(0);
  const emEnvio = useRef(false);
  const lerGuardado = useRef(true);

  // filtros da lista
  const [filtroGrupo, setFiltroGrupo] = useState<string>(TODAS_AS_SITUACOES);
  const [filtroStatus, setFiltroStatus] = useState<FiltroStatus>('todos');
  const [busca, setBusca] = useState('');

  // edição de um caso
  const [editando, setEditando] = useState<Caso | null>(null);
  const [grupoEdicao, setGrupoEdicao] = useState<string | undefined>(undefined);
  const [linhaEdicao, setLinhaEdicao] = useState('');
  const [erroEdicao, setErroEdicao] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setLendo(true);
    try {
      const d = await obter();
      setDados(d);
      setErroLeitura(null);
    } catch (e: any) {
      setErroLeitura({ texto: erroDe(e), status: e?.response?.status ?? null });
    } finally {
      setLendo(false);
    }
  }, []);

  useEffect(() => { if (liberado) carregar(); }, [liberado, carregar]);

  // Abriu a página com uma atualização rodando: volta a acompanhar.
  // Sem nenhuma rodando mas com um número guardado: confere uma vez e limpa.
  useEffect(() => {
    if (!dados) return;
    if (dados.em_andamento) {
      guardarId(dados.em_andamento.id);
      setAtualizacaoId(dados.em_andamento.id);
      setFase((f) => proximoPasso(f, 'retomou'));
      return;
    }
    if (!lerGuardado.current) return;
    lerGuardado.current = false;
    const guardado = lerIdGuardado();
    if (!guardado) return;
    consultarStatus(guardado)
      .then((s) => {
        if (s.status === 'processando') { setAtualizacaoId(guardado); setFase((f) => proximoPasso(f, 'retomou')); } else guardarId(null);
      })
      .catch(() => guardarId(null));
  }, [dados]);

  // Acompanha a atualização: uma consulta por vez, a cada 4 segundos.
  useEffect(() => {
    if (fase !== 'acompanhando' || !atualizacaoId) return undefined;
    const id = atualizacaoId;
    let vivo = true;
    falhasSeguidas.current = 0;
    const consultar = async () => {
      if (!podeConsultar('acompanhando', emConsulta.current)) return;
      emConsulta.current = true;
      try {
        const s = await consultarStatus(id);
        if (!vivo) return;
        setConsultaErro(null);
        falhasSeguidas.current = 0;
        if (s.status === 'pronto') {
          guardarId(null);
          setFase((f) => proximoPasso(f, 'terminou'));
          await carregar();
        } else if (s.status === 'erro') {
          guardarId(null);
          setAviso({ tipo: 'erro', texto: s.erro || 'A atualização não terminou.' });
          setFase((f) => proximoPasso(f, 'falhou'));
          await carregar();
        }
      } catch (e: any) {
        if (!vivo) return;
        if (e?.response?.status === 404) {
          guardarId(null);
          setAviso({ tipo: 'erro', texto: 'Não achei esta atualização. Clique em Atualizar com IA para pedir outra.' });
          setFase((f) => proximoPasso(f, 'falhou'));
        } else {
          falhasSeguidas.current += 1;
          if (falhasSeguidas.current >= MAX_FALHAS_SEGUIDAS) {
            setConsultaErro(null);
            setAviso({ tipo: 'erro', texto: 'Não consegui ver o andamento da atualização. Ela pode ter terminado. Clique em Conferir de novo.', conferir: true });
            setFase((f) => proximoPasso(f, 'falhou'));
          } else {
            setConsultaErro('Não consegui ver o andamento agora. Tento de novo em instantes.');
          }
        }
      } finally {
        emConsulta.current = false;
      }
    };
    consultar();
    const timer = window.setInterval(consultar, INTERVALO_CONSULTA_MS);
    return () => { vivo = false; window.clearInterval(timer); };
  }, [fase, atualizacaoId, carregar]);

  async function pedirAtualizacao() {
    if (emEnvio.current || ocupado(fase) || !dados?.pode.atualizar) return;
    emEnvio.current = true;
    setAviso(null);
    setFase((f) => proximoPasso(f, 'clicou'));
    try {
      const r = await atualizar();
      guardarId(r.id);
      setAtualizacaoId(r.id);
      setFase((f) => proximoPasso(f, 'aceito'));
    } catch (e: any) {
      if (ehConflito(e) || ehEspera(e)) {
        // 409 não é falha: já há uma atualização rodando, e a página passa a acompanhá-la (sem aviso laranja).
        if (ehEspera(e)) setAviso({ tipo: 'aviso', texto: erroDe(e) });
        setFase((f) => proximoPasso(f, 'recusado'));
        if (ehConflito(e)) carregar();
      } else {
        setAviso({ tipo: 'erro', texto: erroDe(e) });
        setFase((f) => proximoPasso(f, 'falhou'));
      }
    } finally {
      emEnvio.current = false;
    }
  }

  function abrirEdicao(c: Caso) {
    setEditando(c);
    setGrupoEdicao(c.grupo || undefined);
    setLinhaEdicao(c.linha || '');
    setErroEdicao(null);
  }

  async function salvarEdicao() {
    if (!editando || !dados) return;
    const problema = erroDaEdicao(grupoEdicao, linhaEdicao, dados.grupos);
    if (problema) { setErroEdicao(problema); return; }
    setSalvando(true);
    setErroEdicao(null);
    try {
      await salvarSituacao({ proposta_id: editando.id, grupo: grupoEdicao as string, linha: linhaEdicao.trim(), situacao_em: editando.situacao_em });
      message.success('Situação salva.');
      setEditando(null);
      await carregar();
    } catch (e: any) {
      if (ehConflito(e)) {
        message.warning(erroDe(e));
        setEditando(null);
        await carregar();
      } else {
        setErroEdicao(erroDe(e));
      }
    } finally {
      setSalvando(false);
    }
  }

  // ── contas da tela ──
  const casos = useMemo(() => dados?.casos || [], [dados]);
  const grupos = useMemo(() => dados?.grupos || [], [dados]);
  const casosFiltrados = useMemo(
    () => filtrarCasos(casos, { grupo: filtroGrupo, status: filtroStatus, busca }),
    [casos, filtroGrupo, filtroStatus, busca],
  );
  const contagem = useMemo(() => contarCasos(casosFiltrados), [casosFiltrados]);
  const opcoesSituacao = useMemo(() => opcoesDeGrupo(grupos, casos), [grupos, casos]);
  // A situação filtrada pode sumir da lista depois de recarregar (ninguém mais nela): volta para "todas".
  useEffect(() => {
    const v = filtroValido(filtroGrupo, opcoesSituacao);
    if (v !== filtroGrupo) setFiltroGrupo(v);
  }, [filtroGrupo, opcoesSituacao]);
  const barras = useMemo(() => barrasDeSituacao(dados?.numeros, grupos), [dados, grupos]);
  const cinco = useMemo(() => maioresSituacoes(dados?.numeros, 5), [dados]);
  const reuniao = dados?.numeros.reuniao;
  const totalReuniao = useMemo(() => totalDaReuniao(reuniao), [reuniao]);
  const segmentosReuniao = useMemo(
    () => [...lista(reuniao?.por_segmento)].sort((a, b) => b.casos - a.casos),
    [reuniao],
  );

  // ── acesso ──
  if (carregandoPerms) return <div style={{ padding: 24 }}><Skeleton active /></div>;
  if (!liberado) {
    return (
      <div style={{ padding: 24 }}>
        {erroPerms ? (
          <Alert
            type="warning" showIcon title="Não consegui conferir o seu acesso."
            description="Foi um problema de conexão, não falta de permissão. Tente de novo em instantes."
            action={<Button size="small" onClick={recarregar}>Tentar de novo</Button>}
          />
        ) : <Alert type="warning" showIcon title="Você não tem acesso a esta página." />}
      </div>
    );
  }

  const retrato = dados?.retrato || null;
  const texto: TextoIa | null = retrato?.texto || null;
  const trabalhando = ocupado(fase);
  const podeAtualizar = !!dados?.pode.atualizar;
  const mudou = !!dados && numerosMudaram(retrato, dados.assinatura_atual);
  const errosDoServidor = dados?.ultimo_erro && !aviso && !trabalhando ? dados.ultimo_erro : null;
  const avisosDoTexto = lista(texto?.avisos);

  // ── colunas ──
  const colunasCasos: TableColumnsType<Caso> = [
    {
      title: 'Situação do caso', key: 'situacao', width: 420,
      render: (_: unknown, c: Caso) => (c.grupo ? (
        <div>
          <Space size={4} wrap>
            <Tag color="blue" style={{ marginInlineEnd: 0 }}>{rotuloGrupo(grupos, c.grupo)}</Tag>
            {c.origem_situacao === 'equipe' ? <Tag style={{ marginInlineEnd: 0 }}>equipe</Tag> : null}
            {c.origem_situacao === 'revisada' ? <Tag color="green" style={{ marginInlineEnd: 0 }}>revisada</Tag> : null}
          </Space>
          {c.linha ? <div style={{ marginTop: 4 }}>{c.linha}</div> : null}
          <Text type="secondary" style={{ fontSize: 12 }}>{c.titulo}</Text>
        </div>
      ) : (
        <div>
          <Text type="secondary">Sem situação ainda — clique em Atualizar com IA.</Text>
          <div><Text type="secondary" style={{ fontSize: 12 }}>{c.titulo}</Text></div>
        </div>
      )),
    },
    { title: 'Cliente', dataIndex: 'cliente', key: 'cliente', width: 180 },
    { title: 'Segmento', dataIndex: 'segmento', key: 'segmento', width: 190 },
    {
      // 18/10/2026: o Lucas quer auditar de onde veio a reunião de cada caso.
      title: 'Reunião antes', key: 'reuniao', width: 160,
      render: (_: unknown, c: Caso) => (c.reuniao ? (
        <Dica title={c.reuniao_fonte === 'anotacao' ? 'Anotação feita no negócio' : c.reuniao_fonte === 'registro' ? 'Nota ou atividade de reunião no negócio, antes do envio' : 'Agenda do Outlook do Lucas: evento com e-mail do cliente nos 120 dias antes do envio'}>
          <div>{c.reuniao === 'com' ? 'Com reunião' : 'Sem reunião registrada'}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{c.reuniao_fonte === 'anotacao' ? 'anotação no negócio' : c.reuniao_fonte === 'registro' ? 'registro no negócio' : 'agenda do Lucas'}</Text>
        </Dica>
      ) : <Text type="secondary">sem como saber</Text>),
    },
    { title: 'Valor', dataIndex: 'valor', key: 'valor', width: 110, align: 'right', render: (v: number | null) => reais(v) },
    {
      title: 'Quando', key: 'quando', width: 130,
      render: (_: unknown, c: Caso) => (
        <div>
          <div>{dataBr(c.data_ref)}</div>
          <Tag color={c.status === 'ganho' ? 'green' : 'red'} style={{ marginInlineEnd: 0 }}>{c.status === 'ganho' ? 'Ganho' : 'Perdido'}</Tag>
        </div>
      ),
    },
  ];
  if (dados?.pode.revisar) {
    colunasCasos.push({
      title: '', key: 'acao', width: 100,
      render: (_: unknown, c: Caso) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => abrirEdicao(c)}>Corrigir</Button>
      ),
    });
  }

  const colunasSegmento: TableColumnsType<(typeof segmentosReuniao)[number]> = [
    { title: 'Segmento', dataIndex: 'segmento', key: 'segmento', width: 230 },
    { title: 'Casos', dataIndex: 'casos', key: 'casos', align: 'right', width: 80 },
    { title: 'Com reunião', dataIndex: 'com', key: 'com', align: 'right', width: 110 },
    { title: 'Fechou com', key: 'fcom', align: 'right', width: 120, render: (_: unknown, r) => fechouTexto(r.com_ganhos, r.com) },
    { title: 'Sem reunião', dataIndex: 'sem', key: 'sem', align: 'right', width: 110 },
    { title: 'Fechou sem', key: 'fsem', align: 'right', width: 120, render: (_: unknown, r) => fechouTexto(r.sem_ganhos, r.sem) },
    { title: 'Sem como saber', dataIndex: 'sem_info', key: 'sem_info', align: 'right', width: 130 },
  ];

  const colunasRelacao: TableColumnsType<NonNullable<typeof reuniao>['por_relacao'][number]> = [
    { title: 'Quem é o cliente', key: 'relacao', width: 230, render: (_: unknown, r) => ROTULO_RELACAO[r.relacao] || r.relacao },
    { title: 'Casos medidos', dataIndex: 'casos', key: 'casos', align: 'right', width: 130 },
    { title: 'Com reunião', dataIndex: 'com', key: 'com', align: 'right', width: 110 },
    { title: 'Fechou com', key: 'fcom', align: 'right', width: 150, render: (_: unknown, r) => fechouDeTexto(r.com_ganhos, r.com) },
    { title: 'Sem reunião', dataIndex: 'sem', key: 'sem', align: 'right', width: 110 },
    { title: 'Fechou sem', key: 'fsem', align: 'right', width: 150, render: (_: unknown, r) => fechouDeTexto(r.sem_ganhos, r.sem) },
  ];

  const colunasTop: TableColumnsType<(typeof cinco)[number]> = [
    { title: 'As 5 que mais se repetem', key: 'grupo', width: 300, render: (_: unknown, r) => rotuloGrupo(grupos, r.grupo, false) },
    { title: 'Casos', dataIndex: 'casos', key: 'casos', align: 'right', width: 80 },
    { title: 'Fecha', key: 'taxa', align: 'right', width: 80, render: (_: unknown, r) => pct(r.taxa) },
    { title: 'Receita fechada', key: 'receita', align: 'right', width: 130, render: (_: unknown, r) => reaisCurto(r.receita) },
    { title: 'Ticket mediano', key: 'mediana', align: 'right', width: 130, render: (_: unknown, r) => (r.ganhos > 0 ? reais(r.mediana) : '—') },
    { title: 'Clientes', dataIndex: 'clientes', key: 'clientes', align: 'right', width: 90 },
    { title: 'Clientes novos ganhos', dataIndex: 'novos_ganhos', key: 'novos_ganhos', align: 'right', width: 150 },
    { title: '3 maiores', key: 'top3', align: 'right', width: 100, render: (_: unknown, r) => (r.ganhos > 0 ? pct(r.top3_pct) : '—') },
  ];

  const candidatos = ordenarCandidatos(texto?.semana3?.candidatos);
  const colunasSemana3: TableColumnsType<CandidatoSemana3> = [
    { title: 'Segmento', dataIndex: 'segmento', key: 'segmento', width: 200, render: (v: string) => <Text strong>{v}</Text> },
    { title: 'Aceite', key: 'aceite', width: 220, render: (_: unknown, c) => <CelulaNota nota={c.aceite?.nota ?? null} motivo={c.aceite?.motivo || ''} /> },
    { title: 'Capacidade', key: 'cap', width: 220, render: (_: unknown, c) => <CelulaNota nota={c.capacidade?.nota ?? null} motivo={c.capacidade?.motivo || ''} /> },
    { title: 'Posicionamento', key: 'pos', width: 220, render: (_: unknown, c) => <CelulaNota nota={c.posicionamento?.nota ?? null} motivo={c.posicionamento?.motivo || ''} /> },
    { title: 'Timing', key: 'tim', width: 220, render: (_: unknown, c) => <CelulaNota nota={c.timing?.nota ?? null} motivo={c.timing?.motivo || ''} /> },
    {
      title: 'Soma', key: 'soma', width: 110, align: 'center',
      render: (_: unknown, c) => {
        const s = somaDasNotas(c);
        return (
          <div>
            <Text strong style={{ fontSize: 16 }}>{s.soma}</Text>
            {s.completa ? null : <div><Text type="secondary" style={{ fontSize: 12 }}>sem a capacidade</Text></div>}
          </div>
        );
      },
    },
  ];

  const t = texto?.teste;

  return (
    <div style={{ padding: '16px 16px 48px' }}>
      {/* a) Cabeçalho */}
      <Text type="secondary" style={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 12 }}>Bcorp · Ciclo 1 · Previsibilidade de demanda</Text>
      <Title level={2} style={{ marginTop: 0 }}>Onde a Hydrodata ganha</Title>
      <Paragraph type="secondary" style={{ maxWidth: 900 }}>
        Veja em quais situações os clientes nos procuram e em quais a gente fecha. Veja também o que isso diz para a semana 3.
        Os números são calculados na hora. Os textos são escritos pela IA quando você clica em Atualizar com IA.
      </Paragraph>

      <Space wrap size={[12, 8]} align="center">
        <Button
          type="primary" size="large" icon={<ThunderboltOutlined />} loading={trabalhando}
          disabled={!podeAtualizar || trabalhando} onClick={pedirAtualizacao}
        >
          Atualizar com IA
        </Button>
        <Text type="secondary">{dados ? linhaDoRetrato(retrato) : 'Carregando…'}</Text>
        {lendo && dados ? <Spin size="small" /> : null}
      </Space>
      {dados && !podeAtualizar ? (
        <div style={{ marginTop: 4 }}><Text type="secondary" style={{ fontSize: 12 }}>Você não tem permissão para pedir a atualização.</Text></div>
      ) : null}

      <Space orientation="vertical" size={12} style={{ display: 'flex', marginTop: 16 }}>
        {trabalhando ? (
          <Alert type="info" showIcon icon={<Spin size="small" />} title={TEXTO_ESPERA} description={consultaErro || undefined} />
        ) : null}
        {aviso ? (
          <Alert
            type={aviso.tipo === 'erro' ? 'error' : 'warning'} showIcon closable onClose={() => setAviso(null)}
            title={aviso.tipo === 'erro' ? 'A atualização não deu certo.' : 'Não foi possível pedir agora.'} description={aviso.texto}
            action={aviso.conferir ? <Button size="small" onClick={() => { setAviso(null); carregar(); }}>Conferir de novo</Button> : undefined}
          />
        ) : null}
        {errosDoServidor ? (
          <Alert
            type="error" showIcon title="A última atualização não deu certo."
            description={`${errosDoServidor.erro}${errosDoServidor.concluido_em ? ` (${textoQuando(errosDoServidor.concluido_em)})` : ''}`}
          />
        ) : null}
        {mudou && !trabalhando ? <Alert type="warning" showIcon title={TEXTO_NUMEROS_MUDARAM} /> : null}
        {avisosDoTexto.length ? (
          <Alert type="info" showIcon title="Avisos da última atualização" description={<ul style={{ margin: 0, paddingLeft: 18 }}>{avisosDoTexto.map((a, i) => <li key={i}>{a}</li>)}</ul>} />
        ) : null}
        {erroLeitura ? (
          <Alert
            type="error" showIcon
            title={erroLeitura.status === 503 ? 'A página ainda está sendo instalada.' : 'Não consegui abrir a página.'}
            description={erroLeitura.texto}
            action={<Button size="small" onClick={carregar}>Tentar de novo</Button>}
          />
        ) : null}
      </Space>

      {!dados && !erroLeitura ? <div style={{ marginTop: 24 }}><Skeleton active paragraph={{ rows: 8 }} /></div> : null}

      {dados ? (
        <>
          {/* b) O que aprendemos */}
          <Secao id="og-aprendemos" titulo="O que aprendemos">
            {texto && retrato?.origem === 'importado' && !veDre ? (
              <Alert type="info" showIcon title="O texto importado fala de dinheiro da empresa e só quem vê o financeiro pode lê-lo. Clique em Atualizar com IA para gerar um texto novo." />
            ) : texto && lista(texto.resumo).length ? (
              <ul style={{ maxWidth: 900, paddingLeft: 20, margin: 0 }}>
                {aprendizadosFin.map((r, i) => <li key={`fin-${i}`} style={{ marginBottom: 6 }}><Text strong>{r}</Text></li>)}
                {lista(texto.resumo).map((r, i) => <li key={i} style={{ marginBottom: 6 }}>{r}</li>)}
              </ul>
            ) : aprendizadosFin.length ? (
              <ul style={{ maxWidth: 900, paddingLeft: 20, margin: 0 }}>
                {aprendizadosFin.map((r, i) => <li key={`fin-${i}`} style={{ marginBottom: 6 }}><Text strong>{r}</Text></li>)}
              </ul>
            ) : <SemTexto />}
          </Secao>

          {/* c) A base (só com financeiro.dre.ver) */}
          <BaseFinanceira onResumo={aoResumoFinanceiro} />

          {/* d) Reunião antes da proposta */}
          <Secao
            id="og-reuniao" etiqueta="Semana 2 · O padrão" titulo="1. Reunião antes da proposta × fechou"
            subtitulo="Só entram na conta de quem fechou os casos em que dá para saber se houve reunião. Os da coluna Sem como saber ficam de fora."
          >
            <Table
              size="small" bordered rowKey="segmento" pagination={false} dataSource={segmentosReuniao} columns={colunasSegmento}
              scroll={{ x: 900 }} locale={{ emptyText: 'Ainda não há casos para mostrar.' }}
              summary={() => (segmentosReuniao.length ? (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}><Text strong>Total</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right"><Text strong>{totalReuniao.casos}</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right"><Text strong>{totalReuniao.com}</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right"><Text strong>{fechouTexto(totalReuniao.com_ganhos, totalReuniao.com)}</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right"><Text strong>{totalReuniao.sem}</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right"><Text strong>{fechouTexto(totalReuniao.sem_ganhos, totalReuniao.sem)}</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right"><Text strong>{totalReuniao.sem_info}</Text></Table.Summary.Cell>
                </Table.Summary.Row>
              ) : null)}
            />
            <div style={{ marginTop: 16 }}>
              <Table
                size="small" bordered rowKey="relacao" pagination={false} dataSource={lista(reuniao?.por_relacao)} columns={colunasRelacao}
                scroll={{ x: 800 }} locale={{ emptyText: 'Ainda não há casos para mostrar.' }}
              />
            </div>
            {texto?.reuniao ? <Paragraph style={{ marginTop: 16, maxWidth: 900 }}>{texto.reuniao}</Paragraph> : null}
            <Paragraph type="secondary" style={{ marginTop: 8, maxWidth: 900 }}>
              Fonte: anotação do negócio, registro de reunião no negócio e a agenda do Outlook do Lucas (importada em 16/10/2026).
              Reunião que não foi registrada não aparece.
            </Paragraph>
          </Secao>

          {/* e) A situação de cada caso */}
          <Secao
            id="og-casos" titulo="2. A situação de cada caso, em uma linha"
            subtitulo="Cada negócio decidido ganhou uma linha. Ela diz a situação que levou o cliente a nos procurar, o pedido, a relação com a Hydrodata e o desfecho. A IA sugere. O comercial confere e corrige o que estiver errado."
          >
            <Space wrap size={[12, 8]} style={{ marginBottom: 12 }}>
              <Select
                aria-label="Situação" value={filtroGrupo} onChange={setFiltroGrupo} options={opcoesSituacao}
                showSearch filterOption={filtrarOpcao} style={{ width: 'min(100%, 360px)', minWidth: 240 }} popupMatchSelectWidth={false}
              />
              <Segmented
                value={filtroStatus} onChange={(v) => setFiltroStatus(v as FiltroStatus)}
                options={[{ value: 'todos', label: 'Todos' }, { value: 'ganho', label: 'Ganhos' }, { value: 'perdido', label: 'Perdidos' }]}
              />
              <Input
                allowClear aria-label="Buscar" placeholder="Buscar por título, cliente ou segmento" value={busca}
                onChange={(e) => setBusca(e.target.value)} style={{ width: 'min(100%, 320px)' }}
              />
            </Space>
            <div style={{ marginBottom: 8 }}><Text strong>{textoContagem(contagem)}</Text></div>
            <Table
              size="small" rowKey="id" dataSource={casosFiltrados} columns={colunasCasos} scroll={{ x: 1000 }}
              pagination={{ pageSize: 50, showSizeChanger: false, hideOnSinglePage: true }}
              locale={{ emptyText: 'Nenhum caso com este filtro.' }}
            />
          </Secao>

          {/* f) As situações que mais se repetem */}
          <Secao id="og-top" titulo="3. As situações que mais se repetem, com casos e receita">
            {barras.length ? (
              <>
                <Row gutter={[24, 24]}>
                  <Col xs={24} xl={12}>
                    <Text strong>Fechamento por situação</Text>
                    <div><Text type="secondary">barra cheia = ganhos; barra clara = perdidos</Text></div>
                    <GraficoFechamento barras={barras} />
                  </Col>
                  <Col xs={24} xl={12}>
                    <Text strong>Receita fechada por situação</Text>
                    <div><Text type="secondary">valor dos negócios ganhos; passe o mouse para ver o peso dos 3 maiores clientes</Text></div>
                    <GraficoReceita barras={barras} />
                  </Col>
                </Row>
                <div style={{ marginTop: 16 }}>
                  <Table
                    size="small" bordered rowKey="grupo" pagination={false} dataSource={cinco} columns={colunasTop} scroll={{ x: 1000 }}
                  />
                </div>
                {texto && lista(texto.situacoes).length ? (
                  <div style={{ marginTop: 16, maxWidth: 900 }}>
                    {lista(texto.situacoes).map((s) => (
                      <Paragraph key={s.grupo}>
                        <Text strong>{rotuloGrupo(grupos, s.grupo)}. </Text>{leituraDaSituacao(texto, s.grupo)}
                      </Paragraph>
                    ))}
                  </div>
                ) : null}
              </>
            ) : <Alert type="info" showIcon title="Ainda não há casos com situação. Clique em Atualizar com IA." />}
          </Secao>

          {/* g) Uma proposta recente: um concorrente assinaria? */}
          <Secao
            id="og-teste" titulo="4. Uma proposta recente: um concorrente assinaria?"
            subtitulo="A IA lê uma proposta recente, sem nome e sem logo. Ela separa o que qualquer concorrente assinaria do que só a Hydrodata pode assinar."
          >
            {t ? (
              <>
                <Card size="small" style={{ background: '#fafafa' }}>
                  <Paragraph style={{ marginBottom: 4 }}>{t.descricao}</Paragraph>
                  <Text type="secondary" style={{ fontSize: 12 }}>Negócio nº {t.negocio_id}</Text>
                </Card>
                <div style={{ margin: '16px 0', padding: '12px 16px', borderLeft: '4px solid #fa8c16', background: '#fff7e6' }}>
                  <Text strong style={{ fontSize: 18 }}>{t.veredito}</Text>
                </div>
                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={12}>
                    <Card size="small" title="O que qualquer um assinaria" style={{ height: '100%' }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>{lista(t.qualquer_um).map((x, i) => <li key={i} style={{ marginBottom: 6 }}>{x}</li>)}</ul>
                    </Card>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Card size="small" title="O que só a Hydrodata pode assinar" style={{ height: '100%' }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>{lista(t.so_nos).map((x, i) => <li key={i} style={{ marginBottom: 6 }}>{x}</li>)}</ul>
                    </Card>
                  </Col>
                </Row>
                {lista(t.contradicoes).length ? (
                  <Space orientation="vertical" size={8} style={{ display: 'flex', marginTop: 16 }}>
                    {lista(t.contradicoes).map((c, i) => <Alert key={i} type="warning" showIcon title="Contradição que pode custar dinheiro" description={c} />)}
                  </Space>
                ) : null}
                <Title level={4} style={{ marginTop: 24 }}>Como a abertura poderia ser (formato da oferta)</Title>
                {/* 16/10/2026: a IA às vezes cita outros clientes e promete aprovação — e este texto é para ir ao cliente. */}
                <Alert
                  type="info" showIcon style={{ marginBottom: 12 }}
                  title="Sugestão da IA. Antes de usar com um cliente, tire o nome de outros clientes e qualquer promessa de aprovação do órgão."
                />
                <Descriptions
                  bordered size="small" column={1} styles={{ label: { width: 'min(40%, 190px)', fontWeight: 600 } }}
                  items={[
                    { key: 'quem', label: 'Para quem e por quê', children: t.abertura?.para_quem },
                    { key: 'risco', label: 'Risco evitado', children: t.abertura?.risco },
                    { key: 'prova', label: 'Prova', children: t.abertura?.prova },
                    { key: 'opcoes', label: 'Opções', children: t.abertura?.opcoes },
                  ]}
                />
              </>
            ) : <SemTexto />}
          </Secao>

          {/* h) Semana 3 */}
          <Secao
            id="og-semana3" etiqueta="Semana 3" titulo="Cabeça de praia (sugestão da IA)"
            subtitulo="Notas de 1 (fraco) a 3 (forte) para cada segmento candidato. A soma ajuda a comparar."
          >
            {texto?.semana3 ? (
              <>
                <Table
                  size="small" bordered rowKey="segmento" pagination={false} dataSource={candidatos} columns={colunasSemana3} scroll={{ x: 1100 }}
                />
                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                  <Col xs={24} lg={12}>
                    <Card size="small" title="Recomendação" style={{ height: '100%' }}>{texto.semana3.recomendacao}</Card>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Card size="small" title="Segundo plano" style={{ height: '100%' }}>{texto.semana3.segundo_plano}</Card>
                  </Col>
                </Row>
                {lista(texto.semana3.frases).length ? (
                  <div style={{ marginTop: 16 }}>
                    <Text strong>Frases de posicionamento</Text>
                    {lista(texto.semana3.frases).map((f, i) => (
                      <div key={i} style={{ marginTop: 8, padding: '10px 16px', borderLeft: '4px solid #1677ff', background: '#e6f4ff', fontSize: 16, maxWidth: 900 }}>
                        {f}
                      </div>
                    ))}
                  </div>
                ) : null}
                <Alert
                  style={{ marginTop: 16 }} type="info" showIcon
                  title="Quem decide: liderança comercial e direção de engenharia. A nota de aceite vem dos dados; as outras são sugestão. Confira as frases antes de usar fora da empresa."
                />
              </>
            ) : <SemTexto />}
          </Secao>

          {/* i) Próximos passos */}
          {texto && lista(texto.proximos_passos).length ? (
            <Secao id="og-passos" titulo="Próximos passos">
              <ol style={{ maxWidth: 900, paddingLeft: 20, margin: 0 }}>
                {lista(texto.proximos_passos).map((p, i) => <li key={i} style={{ marginBottom: 6 }}>{p}</li>)}
              </ol>
            </Secao>
          ) : null}

          <Paragraph type="secondary" style={{ marginTop: 32, maxWidth: 900, fontSize: 12 }}>
            Fontes: ERP Hydrodata (Negócios, Faturamento por setor e Margem por setor) e agenda do Outlook do Lucas.
            Negócios contados: privados, decididos (ganho ou perdido) desde {dados.janela?.desde ? dataBr(dados.janela.desde) : 'janeiro de 2025'},
            sem as perdas da limpeza da carteira e sem escopo que virou outro negócio. Receita = valor do negócio ganho.
          </Paragraph>
        </>
      ) : null}

      <Modal
        open={!!editando} onCancel={() => { if (!salvando) setEditando(null); }} destroyOnHidden maskClosable={false}
        title="Corrigir a situação do caso" width="94vw" style={{ maxWidth: 640, top: 16 }}
        styles={{ body: { maxHeight: 'calc(100vh - 190px)', overflowY: 'auto' } }}
        footer={[
          <Button key="cancelar" onClick={() => setEditando(null)} disabled={salvando}>Cancelar</Button>,
          <Button key="salvar" type="primary" onClick={salvarEdicao} loading={salvando}>Salvar</Button>,
        ]}
      >
        {editando ? (
          <Space orientation="vertical" size={12} style={{ display: 'flex' }}>
            <div>
              <Text strong>{editando.titulo}</Text>
              <div><Text type="secondary">{editando.cliente} · {editando.status === 'ganho' ? 'Ganho' : 'Perdido'}{editando.valor != null ? ` · ${reais(editando.valor)}` : ''}</Text></div>
            </div>
            <div>
              <label htmlFor="og-edicao-grupo"><Text strong>Situação</Text></label>
              <Select
                id="og-edicao-grupo" aria-label="Situação do caso" style={{ width: '100%' }} placeholder="Escolha a situação"
                value={grupoEdicao} onChange={setGrupoEdicao} showSearch filterOption={filtrarOpcao}
                options={grupos.map((g) => ({ value: g.codigo, label: g.nome }))}
              />
            </div>
            <div>
              <label htmlFor="og-edicao-linha"><Text strong>Linha do caso</Text></label>
              <Input.TextArea
                id="og-edicao-linha" value={linhaEdicao} onChange={(e) => setLinhaEdicao(e.target.value)}
                maxLength={LIMITE_LINHA} showCount rows={4}
                placeholder="Situação: pedido. Relação com a Hydrodata; desfecho."
              />
            </div>
            {erroEdicao ? <Alert type="error" showIcon title={erroEdicao} /> : null}
          </Space>
        ) : null}
      </Modal>
    </div>
  );
}
